import React from 'react';
import {
  Building2,
  ExternalLink,
  Globe2,
  Lock,
  Mail,
  MapPin,
  Phone,
  Scale,
  ShieldCheck
} from 'lucide-react';
import { PageHero } from '../components/shared/PageHero';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import {
  PolicyBulletList,
  PolicyCardGrid,
  PolicyDetailList,
  PolicyLastUpdated,
  PolicyRelatedLinks,
  PolicySection
} from '../components/legal/PolicyPrimitives';
import { useSEO } from '../hooks/useSEO';

const LAST_UPDATED = '11 de mayo de 2026';

const summaryCards = [
  {
    label: 'Titular',
    value: 'ZAMELUX GREEN SL',
    description: 'Sociedad titular del sitio y responsable de su gestión corporativa y comercial.'
  },
  {
    label: 'Sitio web',
    value: 'zwheel-shop.com',
    description: 'Página informativa y comercial sujeta a la LSSI-CE y al marco jurídico español.'
  },
  {
    label: 'Atención',
    value: 'L-V 9:00 a 18:00',
    description: 'Horario de atención al cliente para consultas, soporte y gestiones generales.'
  },
  {
    label: 'Contacto',
    value: '+34 93 66 75 437',
    description: 'También disponible el correo info@zwheel.com para comunicaciones generales.'
  }
];

const companyDetails = [
  {
    label: 'Razón social',
    value: 'ZAMELUX GREEN SL'
  },
  {
    label: 'CIF',
    value: 'B67255497'
  },
  {
    label: 'Domicilio social',
    value: 'Carrer de la Riera Roja, 12, 08830 Sant Boi de Llobregat, Barcelona'
  },
  {
    label: 'Registro Mercantil',
    value: 'Tomo 46486, folio 0115, hoja 521568, inscripción 2'
  },
  {
    label: 'Administrador legal',
    value: 'Sra. Shujing Zhang'
  },
  {
    label: 'Web titular',
    value: 'https://zwheel-shop.com/'
  }
];

const useConditions = [
  'La navegación, el acceso y el uso del sitio atribuyen la condición de usuario e implican la aceptación de las presentes condiciones de uso, sin perjuicio de la normativa imperativa que resulte aplicable en cada caso.',
  'El usuario se compromete a utilizar la información, los servicios y los contenidos de forma lícita, diligente y respetuosa con la moral, el orden público y los derechos de terceros.',
  'Quien utilice formularios o áreas de contacto del sitio garantiza la veracidad y licitud de los datos facilitados y asume la responsabilidad derivada de comunicaciones inexactas, fraudulentas o incompletas.',
  'Cualquier uso del sitio que perjudique su funcionamiento, menoscabe la seguridad, vulnere derechos de propiedad intelectual o cause daños a ZAMELUX GREEN SL o a terceros podrá dar lugar a las acciones oportunas.'
];

const availabilityTerms = [
  'Los enlaces a sitios de terceros se facilitan únicamente a efectos informativos o de conveniencia para el usuario. ZAMELUX GREEN SL no examina de forma sistemática ni controla el contenido, disponibilidad, exactitud o legalidad de esos sitios externos.',
  'ZAMELUX GREEN SL ha adoptado medidas razonables para reducir riesgos técnicos derivados de la navegación, pero no puede garantizar la ausencia absoluta de incidencias ajenas a su control ni responder por daños derivados del uso general de Internet.',
  'La empresa se reserva el derecho a modificar, actualizar o retirar contenidos, configuraciones y condiciones del sitio sin necesidad de aviso previo, siendo aplicables las versiones publicadas en cada momento.'
];

const securityTerms = [
  'Cuando el sitio permita procesos de compra o contratación online, el usuario deberá revisar y aceptar las condiciones generales específicas aplicables antes de formalizar el pedido o servicio correspondiente.',
  'El sitio utiliza un certificado SSL (Secure Sockets Layer) para autenticar la conexión y proteger la confidencialidad de la información intercambiada entre el usuario y ZAMELUX GREEN SL.',
  'Las medidas de seguridad adoptadas se orientan a preservar la integridad, autenticidad y privacidad de los datos transmitidos durante la navegación y el uso de formularios o servicios digitales.'
];

const intellectualPropertyTerms = [
  'Todos los contenidos del sitio, incluidos textos, imágenes, diseños, software, estructura, marcas, logotipos, combinaciones gráficas y demás elementos, son titularidad de ZAMELUX GREEN SL o se utilizan con licencia suficiente.',
  'Estos contenidos están protegidos por la normativa española, europea e internacional sobre propiedad intelectual e industrial, por lo que quedan reservados todos los derechos de explotación.',
  'Queda prohibida la reproducción, distribución, comunicación pública, transformación o puesta a disposición de los contenidos con fines comerciales sin autorización previa y expresa del titular.',
  'El usuario podrá visualizar, copiar o imprimir contenidos únicamente para uso personal y privado, siempre que no suprima ni altere avisos legales, elementos de seguridad ni referencias de titularidad.'
];

const jurisdictionTerms = [
  'La relación entre el usuario y ZAMELUX GREEN SL se regirá por la normativa vigente en el territorio español.',
  'La empresa podrá ejercitar las acciones civiles o penales que correspondan frente a usos indebidos del sitio, vulneraciones de sus contenidos o incumplimientos de estas condiciones.',
  'Salvo que la normativa de consumo disponga otra cosa, las partes se someterán a los juzgados y tribunales que resulten competentes conforme a Derecho, tomando como referencia el domicilio de ZAMELUX GREEN SL en Barcelona.'
];

export default function LegalTerms() {
  useSEO({
    title: 'Aviso legal y condiciones de uso',
    description: 'Consulta la información identificativa de ZAMELUX GREEN SL, las condiciones de uso del sitio, la política de enlaces, la seguridad SSL y el régimen de propiedad intelectual aplicable.',
    keywords: 'aviso legal, condiciones de uso, Zwheel, Zamelux, propiedad intelectual, jurisdiccion'
  });

  return (
    <div className="min-h-screen bg-white">
      <PageHero
        tag="Legal"
        title="Aviso legal y condiciones de uso"
        subtitle="Información societaria, reglas de uso del sitio y marco jurídico aplicable a la navegación en ZWHEEL."
      />

      <section className="bg-white py-16 md:py-24">
        <div className="container-max section-padding">
          <div className="mx-auto max-w-5xl space-y-10">
            <PolicyLastUpdated date={LAST_UPDATED} />

            <AnimatedSection>
              <div className="blog-content-clean">
                <p className="text-lg leading-8 text-gray-700">
                  Este aviso legal resume la información exigida por la normativa española aplicable a los
                  servicios de la sociedad de la información y fija las condiciones que regulan el acceso,
                  la navegación y el uso del sitio titularidad de ZAMELUX GREEN SL.
                </p>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.05}>
              <PolicyCardGrid items={summaryCards} />
            </AnimatedSection>

            <PolicySection title="Identificación del titular del sitio" icon={Building2} delay={0.1} tone="muted">
              <p className="mb-6 text-base leading-7 text-gray-700">
                En cumplimiento de la Ley 34/2002, de 11 de julio, de Servicios de la Sociedad de la
                Información y de Comercio Electrónico (LSSI-CE), se informa de los datos identificativos de
                la entidad titular del sitio web.
              </p>
              <PolicyDetailList items={companyDetails} />
              <div className="mt-6 grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-gray-500">
                    <Mail className="h-4 w-4" />
                    Correo general
                  </div>
                  <a
                    href="mailto:info@zwheel.com"
                    className="text-base font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                  >
                    info@zwheel.com
                  </a>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-gray-500">
                    <Phone className="h-4 w-4" />
                    Teléfono
                  </div>
                  <a
                    href="tel:+34936675437"
                    className="text-base font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                  >
                    +34 93 66 75 437
                  </a>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white px-5 py-4">
                  <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-gray-500">
                    <MapPin className="h-4 w-4" />
                    Atención
                  </div>
                  <p className="text-base leading-7 text-gray-700">
                    Días laborables, de lunes a viernes, de 9:00h a 18:00h.
                  </p>
                </div>
              </div>
            </PolicySection>

            <PolicySection title="Condiciones de uso y responsabilidades" icon={ShieldCheck} delay={0.15}>
              <PolicyBulletList items={useConditions} />
            </PolicySection>

            <PolicySection title="Enlaces externos, disponibilidad y modificaciones" icon={Globe2} delay={0.2}>
              <PolicyBulletList items={availabilityTerms} />
            </PolicySection>

            <PolicySection title="Contratación online y seguridad" icon={Lock} delay={0.25} tone="accent">
              <PolicyBulletList items={securityTerms} />
            </PolicySection>

            <PolicySection title="Propiedad intelectual e industrial" icon={ShieldCheck} delay={0.3}>
              <PolicyBulletList items={intellectualPropertyTerms} />
            </PolicySection>

            <PolicySection title="Acciones legales, legislación aplicable y jurisdicción" icon={Scale} delay={0.35}>
              <PolicyBulletList items={jurisdictionTerms} />
              <p className="mt-6 text-base leading-7 text-gray-700">
                Para cualquier consulta adicional sobre este aviso legal o sobre el funcionamiento del sitio,
                puede contactar con ZAMELUX GREEN SL a través de{' '}
                <a
                  href="mailto:info@zwheel.com"
                  className="font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                >
                  info@zwheel.com
                </a>
                .
              </p>
            </PolicySection>

            <PolicyRelatedLinks
              title="Documentos relacionados"
              links={[
                { label: 'Política de privacidad', href: '/legal/privacy' },
                { label: 'Política de cookies', href: '/legal/cookies' },
                { label: 'Contacto', href: '/contacto' }
              ]}
            />

            <AnimatedSection delay={0.4}>
              <div className="rounded-[2rem] border border-gray-200 bg-gray-50 p-8">
                <div className="flex items-start gap-3">
                  <ExternalLink className="mt-1 h-5 w-5 flex-shrink-0 text-black" />
                  <p className="text-sm leading-7 text-gray-600">
                    Si necesita una aclaración adicional sobre el alcance de estas condiciones o sobre una
                    contratación concreta, contacte con el equipo de ZWHEEL antes de finalizar su solicitud.
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
}
